import { Injectable } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { GotoParms } from '@app/core/services/state.service';

@Injectable({
    providedIn: 'root'
})
export class OeeService {
    private reloaded = false;
    private draft?: OeeDataGet;
    private selectedRemark?: OeeRemarkItem;
    private reloadData = false;

    constructor(private datatask: DataTaskService) { }

    /** @datatask 251 */
    public async getData(oeedata_keyno?: string): Promise<OeeDataGet> { return (await this.datatask.Post(251, { oeedata_keyno }))[0]; }
    /** @datatask 250 */
    public async getCurrentRedirect(): Promise<OeeCurrentRedirectGet> { return (await this.datatask.Post(250))[0]; }
    /** @datatask 252 */
    public getStates(equipment_id?: string | number): Promise<Item[]> { return this.datatask.Post(252, equipment_id === undefined ? {} : { equipment_id }); }
    /** @datatask 195 */
    public getRegisteredLabours(webpagename = 'oeedata'): Promise<Item[]> { return this.datatask.Post(195, { webpagename }); }
    /** @datatask 246 */
    public getRemarks(oeestate_keyno: string): Promise<OeeRemarkItem[]> { return this.datatask.Post(246, { oeestate_keyno }); }
    /** @datatask 253 */
    public async selectState(oeestate_keyno: string | number): Promise<void> {
        await this.datatask.Post(253, { oeestate_keyno });
        this.reloadData = true;
    }
    public selectRemark(item: OeeRemarkItem): void { this.selectedRemark = { ...item }; }
    public markLabourSelected(): void { this.reloadData = true; }
    /** @datatask 323 */
    public async getShowSettings(oeestate_keyno: string | number): Promise<OeeShowSettings> { return (await this.datatask.Post(323, { oeestate_keyno }))[0]; }
    /** @datatask 254 */
    public async saveData(page: OeeDataGet): Promise<OeeSaveResponse | undefined> { return (await this.datatask.Post(254, page))[0]; }

    /** @datatask 254 — current view sends only these fields, not the complete editor settings. */
    public saveCurrentData(page: OeeDataGet): Promise<unknown> {
        const fields = ['oeedata_keyno', 'equipment_id', 'equipment_name', 'oeestate_keyno', 'oeestate_name', 'reg_by_labour_no', 'reg_by_labour_name', 'oeestateremark_keyno', 'oeestateremark_name', 'approved_status', 'cycle_time_seconds', 'products_optimal', 'products_real', 'startup_wrecks', 'wrecks', 'prod_id', 'oeestateremark_text', 'start_dt', 'end_dt', 'color', 'approved_datetime', 'approved_by_portal_user_keyno', 'approved_by_portal_user_name'] as const;
        return this.datatask.Post(254, { ...Object.fromEntries(fields.map(field => [field, page[field]])), insert_new_oeedata: '0' });
    }
    /** @datatask 258 */
    public approveData(page: OeeDataGet): Promise<unknown> {
        return this.datatask.Post(258, { oeedata_keyno: page.oeedata_keyno, equipment_id: page.equipment_id });
    }
    /** @datatask 324 */
    public deleteData(oeedata_keyno: string | number): Promise<unknown> { return this.datatask.Post(324, { oeedata_keyno }); }

    public get showRemarkText(): string { return this.selectedRemark?.show_remarktext ?? ''; }

    public async getEditorData(keyno: string): Promise<OeeDataGet | undefined> {
        const draft = this.draft;
        const remark = this.selectedRemark;
        const restore = draft && String(draft.oeedata_keyno) === keyno && remark?.item_id !== undefined && remark.item_id !== '' && !!remark.item_name;
        const page = restore && !this.reloadData ? { ...draft } : await this.getData(keyno);
        if (restore && remark) {
            page.oeestateremark_keyno = remark.item_id;
            page.oeestateremark_name = remark.item_name;
        }
        this.draft = undefined;
        this.reloadData = false;
        return page;
    }
    public prepareSelection(page: OeeDataGet, target: OeeSelectionTarget): void {
        this.draft = JSON.parse(JSON.stringify(page)) as OeeDataGet;
        if (target === 'selectoeestate' && this.selectedRemark) this.selectedRemark = { ...this.selectedRemark, item_id: '', item_name: '' };
    }
    public clearDraft(): void { this.draft = undefined; }

    /**
    * Retrieves list of items.
    *
    * @datatask 256
    * @datataskName sp_ws_p2_oeedashboard_list
    * @param parms settings for the list to retrieve.
    * @returns promise containing list of functions.
    */
    public listOeeDashBoard(): Promise<Item[]> {
        return this.datatask.Post(256);
    }

    public reloadOeeDashboard(): boolean {
        this.reloaded = false;

        setTimeout(() => this.reloaded = true, 30000);

        return this.reloaded;
    }
}

export type OeeSelectionTarget = 'selectoeestate' | 'selectlabourpu' | 'selectoeestateremark';
export interface OeeShowSettings { show_details?: string; show_remark?: string; show_prodid?: string; }
export interface OeeDataGet extends OeeShowSettings {
    oeedata_keyno: string | number;
    equipment_id: string | number;
    equipment_name?: string;
    oeestate_keyno?: string | number;
    oeestate_keyno_new?: string | number;
    oeestate_name?: string;
    reg_by_labour_no?: string | number;
    reg_by_labour_name?: string;
    reg_by_equipment_id?: string | number;
    oeestateremark_keyno?: string | number;
    oeestateremark_name?: string;
    approved_status?: string;
    cycle_time_seconds?: string | number;
    products_optimal?: string | number;
    products_real?: string | number;
    startup_wrecks?: string | number;
    wrecks?: string | number;
    prod_id?: string;
    oeestateremark_text?: string;
    start_dt?: string;
    end_dt?: string;
    color?: string;
    approved_by_portal_user_keyno?: string | number;
    approved_by_portal_user_name?: string;
    approved_datetime?: string;
    show_savebutton_change?: string;
    show_remarktext?: string;
    show_insert_new_oeedata?: string;
    insert_new_oeedata?: string;
    oee_save_possible?: string;
}
export interface OeeCurrentRedirectGet { webpagename: string; parms: GotoParms<unknown>; oeedata_keyno?: string; }
export interface OeeSaveResponse { saveok: string; promptmessage?: string; }
export interface OeeRemarkItem extends Item { show_remarktext?: string; }
